import type { FindFn } from "@shared/models/analytics/base"
import type { FollowedCourse } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { partial } from "lodash-es"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import type { Narrative } from "@shared/actions/create-narrative"
import { followContext, followedCourseEvent } from "./COURSE_FOLLOW_COURSE"
import defaultCourseActivity from "./default-course-activity"
import defaultCourseContext from "./default-course-context"
import defaultCourseModels from "./default-course-models"

interface Payload {
  planbookId: string
  courseId: string
  courseTitle: string
  originalFollowedCourseInfo: FollowedCourse | null
  /** Null when the followed class isn't loaded. */
  originalFollowedCourseTitle: string | null
}

export function prepareCourseUnfollowCourse(payload: Payload): PreparedAction {
  return {
    name: "COURSE_UNFOLLOW_COURSE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  return [Patch.create("course", payload.courseId).set("attributes.followedCourse", null).patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  return [
    Patch.create("course", payload.courseId).set(
      "attributes.followedCourse",
      payload.originalFollowedCourseInfo,
    ).patch,
  ]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseModels(payload, findFn, userId)

  let context = {
    ...defaultCourseContext(payload, models),
    ...followContext({
      followingCourseId: payload.courseId,
      followedCourseId: payload.originalFollowedCourseInfo?.courseId ?? null,
    }),
    followedCourseInfo: null,
  }

  const followed = payload.originalFollowedCourseTitle
    ? `"${payload.originalFollowedCourseTitle}"`
    : "a class"

  return {
    title: "Stopped Following Class",
    titleForAnalytics: "Course - Changed Followed Course",
    description: `Your "${payload.courseTitle}" class stopped following ${followed}`,
    context,
    activity: defaultCourseActivity(payload, models),
    newAnalyticsEvents: [
      followedCourseEvent(findFn, userId, "removing", {
        planbookId: payload.planbookId,
        courseId: payload.courseId,
        followedCourseId: payload.originalFollowedCourseInfo?.courseId ?? null,
      }),
    ],
  }
}
